import React, { useCallback, useState, memo, useEffect } from 'react';
import { styled } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl-next';
import { Subscribe } from 'unstated';

import { token } from '@atlaskit/tokens';
import { N0, N60 } from '@atlaskit/theme/colors';
import AlignLeftIcon from '@atlaskit/icon/core/migration/align-left--editor-align-left';
import AlignCenterIcon from '@atlaskit/icon/core/migration/align-center--editor-align-center';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Box, xcss } from '@atlaskit/primitives';

import { EditorAppearanceButtonNext } from '@confluence/editor-appearance-button';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import { usePubSubEvent } from '@confluence/pubsub-client';
import {
	useTitleContentPropertiesForEditor,
	TitleAlignmentType,
	COVERPICTUREWIDTH,
} from '@confluence/content-topper';
import { useIsEditorPluginAIEnabled } from '@confluence/editor-features';
import { MIN_DYNAMIC_COVER_IMAGE_HEIGHT } from '@confluence/page-cover-picture';

import { CoverPictureAndButton } from './CoverPictureAndButton';
import { EmojiButton } from './EmojiButton';
import { ContentStateHoverButton } from './ContentStateHoverButton';
import { AlignmentButton } from './AlignmentButton';
import { AlignmentButtonContainer } from './AlignmentButtonContainer';
import type {
	FloatingToolbarButtonStyleType,
	ToolbarAboveTitleProps,
} from './titleareaContentComponentsTypes';
import { SuggestTitleButton } from './SuggestTitleButton';
import { AISuggestedImageHeaderLoading } from './AISuggestedImageHeaderLoading';

const toolbarAboveTitleBoxStylesBase = xcss({
	width: '100%',
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
});

const toolbarAboveTitleBoxStylesMarginBottomSpace050 = xcss({
	marginBottom: 'space.050',
});

const coverPictureAndButtonWithCoverPictureBoxStyles = xcss({
	width: '100%',
	marginBottom: 'space.150',
});

const toolbarStyle = xcss({
	// @ts-ignore TODO: refactor to align this to the design system
	marginTop: '-22px',
});

const toolbarPlaceholderBoxStylesMarginBottomSpace0 = xcss({
	marginBottom: 'space.0',
});

const toolbarPlaceholderBoxStylesMarginBottomSpace500 = xcss({
	marginBottom: 'space.500',
});

const leftAlignmentsButtonBoxXcss = xcss({
	paddingRight: 'space.050',
});

const editorAppearanceButtonBoxXcss = xcss({
	marginLeft: 'space.negative.075',
	marginRight: 'space.025',
});

export const i18n = defineMessages({
	leftAlignmentIconLabel: {
		id: 'title-area-content-components.toolbar-above-title.left-alignment-icon.label',
		defaultMessage: 'Left Alignment Icon',
		description: 'Label for the left alignment icon in floating toolbar above title',
	},
	centerAlignmentIconLabel: {
		id: 'title-area-content-components.toolbar-above-title.center-alignment-icon.label',
		defaultMessage: 'Center Alignment Icon',
		description: 'Label for the center alignment icon in floating toolbar above title',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const VerticalLineWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const VerticalLine = styled.span({
	width: '24px',
	height: '0px',
	transform: 'rotate(90deg)',
	border: `0.5px solid ${token('color.border', N60)}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ToolbarButtonsWrapper = styled.div<{
	isUploadingPicture: boolean;
	hasCoverPicture: boolean;
	shouldShowToolbar: boolean;
	isTitleCenterAligned?: boolean;
}>({
	background: token('elevation.surface', N0),
	position: 'relative',
	height: 'fit-content',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	alignSelf: ({ hasCoverPicture, isUploadingPicture, isTitleCenterAligned }) =>
		(hasCoverPicture || isUploadingPicture) && isTitleCenterAligned ? 'center' : 'initial',
	width: 'fit-content',
	borderRadius: '3px',
	padding: token('space.050', '4px'),
	boxShadow: token(
		'elevation.shadow.overlay',
		'0px 0px 1px 0px rgba(9, 30, 66, 0.31), 0px 10px 18px 0px rgba(9, 30, 66, 0.15)',
	),
	zIndex: 200,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	display: ({ shouldShowToolbar }) => (shouldShowToolbar ? 'flex' : 'none'),
});

const floatingToolbarButtonStyle: FloatingToolbarButtonStyleType = {
	height: '32px',
	alignItems: 'center',
};

export const ToolbarAboveTitle = memo(
	({
		contentId,
		userOffline,
		isUnpublishedDraft,
		isTitleDisabled,
		isTitleHovered,
		isCoverPictureModalOpen,
		hasCoverPicture,
		isUploadingPicture,
		isAddHeaderImageButtonFocused,
		setSelectedEmoji,
		onEmojiPickerLoaded,
		onUpdateStartEmoji,
		onUpdateEndEmoji,
		isUpdatingEmoji,
		isEmojiButtonFocused,
		setIsTitleHovered,
		contentType,
		isPublishingContent,
		hasContentState,
		contentStateState,
		isTitleCenterAligned,
		alignmentButtonState,
		coverPictureState,
		isFirstEmojiPickerOpen,
		isChangingPicture,
		flags,
		isRemovingEmoji,
		isReadOnly,
		isEditorEmpty,
		coverPictureWidth,
		spaceKey,
		contentComponentsDisabled,
		isStatusDisconnected,
		isAIUnsplashImage,
		aiSuggestedUnsplashImages,
	}: ToolbarAboveTitleProps) => {
		const { createAnalyticsEvent } = useAnalyticsEvents();

		const isLivePage = useIsCurrentPageLive();

		const isFixedWidth = coverPictureWidth === COVERPICTUREWIDTH.FIXED;

		const isCoverPictureAreaRendered = isUploadingPicture || hasCoverPicture;

		const handleStatusButtonOnMouseEnter = useCallback(() => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'hovered',
					source: 'statusButton',
					actionSubject: 'pageTitle',
					actionSubjectId: 'addPageStatusButton',
				},
			}).fire();
		}, [createAnalyticsEvent]);

		const createAlignmentButtonAnalytics = useCallback(
			(alignment: string) => {
				const alignmentName = alignment.charAt(0).toUpperCase() + alignment.slice(1);
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'clicked',
						source: `${alignment}AlignmentButton`,
						actionSubject: 'button',
						actionSubjectId: `pageTitle${alignmentName}AlignButton`,
					},
				}).fire();
			},
			[createAnalyticsEvent],
		);

		const intl = useIntl();
		const [isContentStateButtonFocused, setIsContentStateButtonFocused] = useState(false);
		const [isContentStateMenuOpen, setIsContentStateMenuOpen] = useState(false);

		const [isMoreOptionsButtonFocused, setIsMoreOptionsButtonFocused] = useState(false);
		// Tracks if any of the hover buttons are focused (means user tabbed to a hover button)
		const [isHoverButtonFocused, _setIsHoverButtonFocused] = useState<boolean>(
			isAddHeaderImageButtonFocused ||
				isEmojiButtonFocused ||
				isContentStateButtonFocused ||
				isMoreOptionsButtonFocused,
		);

		const onlineShouldShowToolbar =
			!isFirstEmojiPickerOpen &&
			!isChangingPicture &&
			(isTitleHovered || isHoverButtonFocused || isContentStateMenuOpen || isCoverPictureModalOpen);

		const onlineShouldShowHoverButton =
			isTitleHovered || isHoverButtonFocused || isContentStateMenuOpen || isCoverPictureModalOpen;

		const [shouldShowToolbarPrevOnlineValue, setShouldShowToolbarPrevOnlineValue] =
			useState<boolean>(onlineShouldShowToolbar);
		const [shouldShowHoverButtonPrevOnlineValue, setShouldShowHoverButtonPrevOnlineValue] =
			useState<boolean>(onlineShouldShowHoverButton);

		useEffect(() => {
			if (!userOffline) {
				setShouldShowToolbarPrevOnlineValue(onlineShouldShowToolbar);
				setShouldShowHoverButtonPrevOnlineValue(onlineShouldShowHoverButton);
			}
		}, [userOffline, onlineShouldShowHoverButton, onlineShouldShowToolbar]);

		const shouldShowToolbar =
			!isReadOnly && (userOffline ? shouldShowToolbarPrevOnlineValue : onlineShouldShowToolbar);
		const shouldShowHoverButton =
			!isReadOnly &&
			(userOffline ? shouldShowHoverButtonPrevOnlineValue : onlineShouldShowHoverButton);

		/* These states are used to track width of emoji button and status button since the width of the button differs with language.
  Due to the absolute positioning, the buttons can overlap if a page does not have a content state or header button.
  These widths are passed to the content state button and more options button so they can space themselves accordingly.
   */
		const [emojiButtonWidth, setEmojiButtonWidth] = useState<number>(0);
		useEffect(() => {
			return () => {
				// Whenever we leave the editor, we need to clean up and reset the content state container values on unmount,
				// otherwise when the user opens editor, previous values are incorrectly persisted (could also do this in breadcrumbs but chose here)
				void contentStateState.resetToDefaults();
				void coverPictureState.resetToDefaults();
				void alignmentButtonState?.resetToDefaults();
			};
		}, [contentStateState, coverPictureState, alignmentButtonState]);

		const updateAlignmentState = alignmentButtonState?.toggleAlignmentButton;
		const updateCoverPictureWidthState = coverPictureState?.toggleCoverPictureWidthButton;

		const { updatePageTitleProperty, isUpdatingAlignment } = useTitleContentPropertiesForEditor({
			contentId,
			updateAlignmentState,
			updateCoverPictureWidthState,
			contentType,
			flags,
		});

		const onAlignmentButtonClick = async (
			alignmentButtonContainer: AlignmentButtonContainer,
			alignment: TitleAlignmentType,
		) => {
			if (
				alignmentButtonContainer.state.alignmentButtonState === alignment ||
				isUpdatingAlignment
			) {
				// don't mutate state since it's the current state or it's already trying to mutate the state
				return;
			}
			void alignmentButtonContainer.setIsUpdatingAlignment(true);
			void alignmentButtonContainer.toggleAlignmentButton(alignment);
			await updatePageTitleProperty(JSON.stringify({ titleLayoutAlignment: alignment }));
			void alignmentButtonContainer.setIsUpdatingAlignment(false);
		};

		const editorAppearanceButton = (
			<EditorAppearanceButtonNext
				contentId={contentId}
				disabled={!!contentComponentsDisabled}
				isStatusDisconnected={!!isStatusDisconnected}
			/>
		);

		const emojiButton = (
			<EmojiButton
				contentId={contentId}
				userOffline={userOffline}
				isUnpublishedDraft={isUnpublishedDraft}
				isTitleDisabled={isTitleDisabled}
				shouldShowHoverButton={shouldShowHoverButton}
				isCoverPictureModalOpen={isCoverPictureModalOpen}
				setSelectedEmoji={setSelectedEmoji}
				onEmojiPickerLoaded={onEmojiPickerLoaded}
				onUpdateStartEmoji={onUpdateStartEmoji}
				onUpdateEndEmoji={onUpdateEndEmoji}
				isUpdatingEmoji={isUpdatingEmoji}
				setIsTitleHovered={setIsTitleHovered}
				floatingToolbarButtonStyle={floatingToolbarButtonStyle}
				isRemovingEmoji={isRemovingEmoji}
			/>
		);

		const [coverPictureAndButtonWrapperHeight, setCoverPictureAndButtonWrapperHeight] = useState(
			MIN_DYNAMIC_COVER_IMAGE_HEIGHT,
		);

		const aiSuggestedImageHeader = (
			<AISuggestedImageHeaderLoading
				isUploadingPicture={isUploadingPicture}
				isAIUnsplashImage={isAIUnsplashImage}
				isFixedWidthImageOption={isFixedWidth}
			/>
		);

		const coverPicAndButton = (
			<CoverPictureAndButton
				contentId={contentId}
				contentType={contentType}
				isPublishingContent={isPublishingContent}
				userOffline={userOffline}
				hasCoverPicture={hasCoverPicture}
				isUploadingPicture={isUploadingPicture}
				shouldShowHoverButton={shouldShowHoverButton}
				hasContentState={hasContentState}
				isUnpublishedDraft={isUnpublishedDraft}
				floatingToolbarButtonStyle={floatingToolbarButtonStyle}
				isFixedWidthImageOption={isFixedWidth}
				updateHeight={setCoverPictureAndButtonWrapperHeight}
				currentHeight={coverPictureAndButtonWrapperHeight}
				isReadOnly={isReadOnly}
				spaceKey={spaceKey}
				aiSuggestedUnsplashImages={aiSuggestedUnsplashImages}
				isAIUnsplashImage={isAIUnsplashImage}
			/>
		);

		const contentStateHoverButton = (
			<ContentStateHoverButton
				contentId={contentId}
				shouldShowHoverButton={shouldShowHoverButton}
				hasContentState={hasContentState}
				contentType={contentType}
				userOffline={userOffline}
				setIsContentStateButtonFocused={setIsContentStateButtonFocused}
				setIsContentStateMenuOpen={setIsContentStateMenuOpen}
				floatingToolbarButtonStyle={floatingToolbarButtonStyle}
			/>
		);

		// https://reactjs.org/docs/hooks-faq.html#how-can-i-measure-a-dom-node
		const emojiButtonMeasuredRef = useCallback(
			(node: any) => {
				if (node !== null) {
					const width = node.offsetWidth;
					setEmojiButtonWidth(width);
				}
			},
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[emojiButton],
		);

		// Putting pubsub subscription in this file since putting it in the contentState component won't work due to re-renders
		// from unmounting and mounting from the breadcrumbs component and contentStateHoverButton component
		const onContentStateUpdated = useCallback(
			(_eventName: any, pubSubData: any) => {
				const newContentState = JSON.parse(
					pubSubData.contentPropertyUpdateRequestBody.value,
				).contentState;
				void contentStateState.setContentState(newContentState);
			},
			[contentStateState],
		);

		const onContentStateDeleted = useCallback(
			(_eventName: any, _pubSubData: any) => {
				void contentStateState.setContentState(null);
			},
			[contentStateState],
		);

		usePubSubEvent({
			contentId,
			contentType: contentType || 'page',
			eventName: 'updated:contentState',
			onEvent: onContentStateUpdated,
		});

		usePubSubEvent({
			contentId,
			contentType: contentType || 'page',
			eventName: 'deleted:contentState',
			onEvent: onContentStateDeleted,
		});

		const editorAiEnabled = useIsEditorPluginAIEnabled();

		const leftPositionAtEmojiButtonInlineCss = isCoverPictureAreaRendered
			? // This is a dynamic value so tokens cannot be used here
				// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
				{ left: `${emojiButtonWidth}px` }
			: {};

		const coverPictureAndButtonInlineCss =
			isUploadingPicture || hasCoverPicture
				? {
						height: `${coverPictureAndButtonWrapperHeight}px`,
					}
				: {};

		return (
			<Subscribe to={[AlignmentButtonContainer]}>
				{(alignmentButtonContainer: AlignmentButtonContainer) => {
					return (
						<Box
							xcss={[
								toolbarAboveTitleBoxStylesBase,
								toolbarAboveTitleBoxStylesMarginBottomSpace050,
							]}
							testId={
								isTitleCenterAligned
									? 'toolbar-above-title-wrapper-centered'
									: 'toolbar-above-title-wrapper'
							}
						>
							<Box>{aiSuggestedImageHeader}</Box>
							{hasCoverPicture || isUploadingPicture || aiSuggestedUnsplashImages?.[0] ? (
								<Box
									xcss={[
										toolbarStyle,
										(isUploadingPicture || hasCoverPicture) &&
											coverPictureAndButtonWithCoverPictureBoxStyles,
									]}
									// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
									style={coverPictureAndButtonInlineCss}
									testId="cover-pic-and-button-wrapper"
									data-id="cover-pic-and-button-wrapper"
								>
									{coverPicAndButton}
								</Box>
							) : null}
							<Box
								xcss={
									shouldShowToolbar
										? toolbarPlaceholderBoxStylesMarginBottomSpace0
										: toolbarPlaceholderBoxStylesMarginBottomSpace500
								}
							/>
							<ToolbarButtonsWrapper
								isUploadingPicture={isUploadingPicture}
								hasCoverPicture={hasCoverPicture}
								isTitleCenterAligned={isTitleCenterAligned}
								shouldShowToolbar={shouldShowToolbar}
							>
								<>
									<Box
										xcss={leftAlignmentsButtonBoxXcss}
										// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
										style={leftPositionAtEmojiButtonInlineCss}
										testId="left-alignment-button-wrapper"
									>
										<AlignmentButton
											setIsMoreOptionsButtonFocused={setIsMoreOptionsButtonFocused}
											isDisabled={alignmentButtonContainer.state.isUpdatingAlignment}
											floatingToolbarButtonStyle={floatingToolbarButtonStyle}
											userOffline={userOffline}
											handleClick={() => {
												createAlignmentButtonAnalytics(TitleAlignmentType.LEFT.toLowerCase());
												void onAlignmentButtonClick(
													alignmentButtonContainer,
													TitleAlignmentType.LEFT,
												);
											}}
											isSelected={
												alignmentButtonContainer.state.alignmentButtonState ===
												TitleAlignmentType.LEFT
											}
											icon={
												<AlignLeftIcon
													label={intl.formatMessage(i18n.leftAlignmentIconLabel)}
													spacing="spacious"
												/>
											}
										/>
									</Box>
									<Box
										// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
										style={leftPositionAtEmojiButtonInlineCss}
										testId="center-alignment-button-wrapper"
									>
										<AlignmentButton
											setIsMoreOptionsButtonFocused={setIsMoreOptionsButtonFocused}
											isDisabled={alignmentButtonContainer.state.isUpdatingAlignment}
											floatingToolbarButtonStyle={floatingToolbarButtonStyle}
											userOffline={userOffline}
											handleClick={() => {
												createAlignmentButtonAnalytics(TitleAlignmentType.CENTER.toLowerCase());
												void onAlignmentButtonClick(
													alignmentButtonContainer,
													TitleAlignmentType.CENTER,
												);
											}}
											isSelected={
												alignmentButtonContainer.state.alignmentButtonState ===
												TitleAlignmentType.CENTER
											}
											icon={
												<AlignCenterIcon
													label={intl.formatMessage(i18n.centerAlignmentIconLabel)}
													spacing="spacious"
												/>
											}
										/>
									</Box>
									<VerticalLineWrapper>
										<VerticalLine />
									</VerticalLineWrapper>
								</>

								<div ref={emojiButtonMeasuredRef} data-testid="emoji-button-wrapper">
									{emojiButton}
								</div>
								<Box
									// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
									style={leftPositionAtEmojiButtonInlineCss}
									testId="content-state-button-wrapper"
									onMouseEnter={handleStatusButtonOnMouseEnter}
								>
									{contentStateHoverButton}
								</Box>
								{hasCoverPicture || isUploadingPicture ? null : (
									<div data-testid="cover-pic-and-button-wrapper">{coverPicAndButton}</div>
								)}
								{editorAiEnabled && (
									<SuggestTitleButton
										isEditorEmpty={isEditorEmpty}
										floatingToolbarButtonStyle={floatingToolbarButtonStyle}
									/>
								)}
								{isLivePage && (
									<>
										<VerticalLineWrapper>
											<VerticalLine />
										</VerticalLineWrapper>
										<Box
											xcss={editorAppearanceButtonBoxXcss}
											testId="editor-appearance-button-wrapper"
										>
											{editorAppearanceButton}
										</Box>
									</>
								)}
							</ToolbarButtonsWrapper>
						</Box>
					);
				}}
			</Subscribe>
		);
	},
);
